import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ListChecks, Shuffle } from 'lucide-react';
import nurseryService from '../../../services/nursery';
import { errorMessage } from '../../../utils/apiResponse';
import { cn } from '../../../utils/cn';
import { type NurseryLoad, type PickMode } from '../../../types/nursery';

/**
 * How a load is picked, on its page. "Allocate first" is the pick list of
 * allocations; "Pickers choose the lots" allocates nothing and shows each
 * tree with every place it stands at the despatch farm.
 *
 * It can go to the pickers choosing until the load leaves, picking started
 * or not: trees picked stay picked and the rest is picked freely. Back to
 * allocating first only while nothing is picked. The server says which
 * (`can_change_pick_mode`). Switching to
 * the pickers choosing gives back what the load's orders had allocated;
 * switching back allocates them from despatchable places, as confirming does.
 */

const MODES: { mode: PickMode; icon: typeof ListChecks }[] = [
  { mode: 'free', icon: Shuffle },
  { mode: 'allocated', icon: ListChecks },
];

export interface PickModeCardProps {
  load: NurseryLoad;
  /** No tree picked yet, so the mode can still change. */
  editable: boolean;
  onChanged: (load: NurseryLoad) => void;
}

export function PickModeCard({ load, editable, onChanged }: PickModeCardProps) {
  const { t } = useTranslation('nursery', { keyPrefix: 'PickModeCard' });
  const current: PickMode = load.pick_mode ?? 'free';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const hasOrders = (load.orders ?? []).length > 0;
  const modeText = (mode: PickMode) => t(`modeText.${mode}`, { farm: load.despatch_farm_name });

  const choose = async (mode: PickMode) => {
    if (mode === current || busy) return;
    if (mode === 'free' && hasOrders
      && !window.confirm(t('confirmFree'))) return;
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const res = await nurseryService.setLoadPickMode(load.id, mode);
      if (res.success) {
        onChanged(res.data);
        const released = res.data.released_trees ?? 0;
        setNote(mode === 'free'
          ? (released ? t('released', { count: released, n: released.toLocaleString() }) : null)
          : (hasOrders ? t('allocatedNote') : null));
      } else {
        setError(errorMessage(res, t('changeFailed')));
      }
    } catch (e) {
      setError(errorMessage(e, t('changeFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white border border-neutral-200 rounded-xl p-4" aria-labelledby="load-pick-mode">
      <h2 id="load-pick-mode" className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">{t('title')}</h2>
      {editable ? (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup" aria-label={t('groupLabel')}>
          {MODES.map(({ mode, icon: Icon }) => {
            const on = mode === current;
            return (
              <button
                key={mode}
                type="button"
                role="radio"
                aria-checked={on}
                disabled={busy}
                onClick={() => choose(mode)}
                className={cn('text-left rounded-lg border p-3 text-sm transition-colors min-w-0',
                  on ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500' : 'border-neutral-200 hover:border-primary-300')}
              >
                <span className="flex items-center gap-2 font-medium text-neutral-900">
                  <Icon className="h-4 w-4 flex-shrink-0" /> {t(`mode.${mode}`)}
                </span>
                <span className="block mt-1 text-xs text-neutral-600 break-words">{modeText(mode)}</span>
              </button>
            );
          })}
        </div>
      ) : (
        <p className="mt-2 text-sm text-neutral-800 break-words">
          <span className="font-medium">{t('currentMode', { mode: t(`mode.${current}`) })}</span>{' '}
          {modeText(current)}
        </p>
      )}
      {note && <p className="mt-2 text-sm text-green-800 break-words">{note}</p>}
      {error && <p className="mt-2 text-sm text-red-700 break-words">{error}</p>}
    </section>
  );
}

export default PickModeCard;
